import AsyncStorage from '@react-native-async-storage/async-storage';
import { DeviceEventEmitter, Platform } from 'react-native';

import biometricService from '../biometricService';
import { clearCachedSalt, deleteUserSalt } from '../encryption/UserSalt';
import errorLoggingService from '../errorLoggingService';
import {
  removeAllCachedProfileImages,
  removeCachedProfileImage,
} from '../profileImageCache';
import { sessionManager } from '../sessionManager';
import {
  SecureStorage,
  SecureStorageKeys,
} from '../storage/SecureStorage';
import { createLogger } from '../../utils/logger';
import { endServerSession } from './serverSession';
import { SESSION_ENDED_EVENT } from './sessionEvents';
import { settleLetterDraftsAtSignOut } from '../letterDraftMirror';
import { discardCachedVaultKeys, prepareVaultKeysForSignOut } from '../encryption/vaultKeyCache';
import { clearCredentialKeyCache } from '../encryption/credentialFields';
import { clearTrustedPartyAccessCache } from '../encryption/TrustedPartyAccess';
import { LEGACY_TOKEN_KEYS, removeAllTokens } from './tokenStorage';

const logger = createLogger('logoutWipe');

/**
 * AsyncStorage keys wiped on ordinary logout. These are session-scoped
 * identity, caches, and legacy plaintext residue from earlier releases —
 * everything that should be gone between a logout and the next login.
 *
 * Intentionally NOT included here:
 *  - `@email_verified`, `@2fa_setup_complete` — the Splash/AppNavigator
 *    uses these to route a returning user to Login (not SignUp). Clearing
 *    them would force every logged-out user back through signup.
 *  - `task_declined_*` — user preferences that should persist.
 *  - `local_documents` / `local_files` — encrypted document metadata.
 *    Stored by `LocalDocumentStorage`, which persists the fileUrl + a
 *    per-document encryption key (wrapped under the master key). Needed
 *    between logout and re-login so a returning user can still see their
 *    own documents. Full encrypted-metadata storage (SQLCipher or similar)
 *    was evaluated and deferred — see KNOWN_LIMITATIONS.md under "Offline
 *    writes are no longer supported for vault-managed secrets" for the
 *    decision and trade-offs.
 */
const LOGOUT_ASYNC_KEYS = [
  // Session-identity fields
  'user',
  '@user_id',
  '@user_email',
  'hasLoggedIn',
  // Signup / onboarding state
  '@signup_email',
  '@signup_phone',
  'hasCompletedOnboarding',
  '@invitation_token',
  // 2FA state that's session-specific (setup-complete/email-verified are
  // preserved — see above — so the user doesn't bounce back to SignUp)
  '@2fa_verified',
  '@totp_skipped',
  // Biometric opt-in toggle (the keychain credential itself is handled via
  // biometricService.clearStoredCredentials in wipeAllUserData)
  'biometricEnabled',
  // Device-trust metadata (the token itself lives in the keychain)
  '@trusted_devices',
  '@device_fallback_id',
  // Miscellaneous session caches
  'pendingFeedback',
  '@analytics_first_dashboard_seen',
  // Push-notification token cache (server-side registration is cleared
  // separately by pushNotificationService.removeToken())
  'fcmToken',
  // Subscription state that should be re-fetched per login
  'subscriptionState',
  // Legacy vault-credential caches — plaintext fallbacks deleted in Step 4
  // but old installs may still have residue on disk
  'passwords',
  'vault_master_key',
  'vault_document_keys',
  'spouseCredentials',
  'loyaltyPoints',
  'contacts_data',
  // Error-log buffer (ErrorLoggingService also exposes clearStoredErrors,
  // called below — but listed here for belt-and-braces on partial failures)
  '@error_logs',
  // VaultMigration state — once a user completes migration and logs out,
  // they should not be re-routed through MigrationScreen on the next login
  'migration_v1_to_v2_state',
] as const;

/**
 * Additional keys cleared on account deletion — removing these resets the
 * install to "fresh" state, including the account-existence flags that
 * keep a returning user on Login instead of SignUp.
 */
const ACCOUNT_DELETION_ASYNC_KEYS = [
  '@email_verified',
  '@2fa_setup_complete',
] as const;

async function clearAsyncStorageKeys(extra: readonly string[] = []): Promise<void> {
  const all = new Set<string>([
    ...LOGOUT_ASYNC_KEYS,
    ...LEGACY_TOKEN_KEYS,
    ...extra,
  ]);
  try {
    await AsyncStorage.multiRemove(Array.from(all));
  } catch (err) {
    // multiRemove is best-effort; a single failing key should not abort the
    // remaining wipe steps. Log without the key value.
    logger.error('multiRemove failed during logout wipe');
  }
}

async function clearSecureStorageTokens(keepDeviceTrust = false): Promise<void> {
  await Promise.all([
    SecureStorage.removeItem(SecureStorageKeys.authToken),
    SecureStorage.removeItem(SecureStorageKeys.refreshToken),
    keepDeviceTrust ? Promise.resolve() : SecureStorage.removeItem(SecureStorageKeys.deviceTrustToken),
  ]);
}

async function clearInMemoryCaches(): Promise<void> {
  // DocumentEncryptionV2 is stateless; the module-level cached secret
  // material is the per-user PBKDF2 salt, the derived credential keys (K5)
  // and any other owner's master key a trusted party opened.
  clearCachedSalt();
  clearCredentialKeyCache();
  clearTrustedPartyAccessCache();
  // sessionManager schedules a setTimeout for the next refresh; kill it.
  try {
    sessionManager.cleanup();
  } catch {
    /* no-op */
  }
}

/**
 * Session-only wipe. Call on:
 *   - user-initiated logout (Profile screen)
 *   - session-expired 401 auto-logout (http interceptor)
 *   - switching accounts
 *
 * Clears session tokens (keychain + legacy AsyncStorage copies), session
 * state flags (AsyncStorage), the errorLoggingService buffer, and
 * in-memory caches (salt, refresh timer).
 *
 * Preserves the per-user salt in the keychain and the biometric-login
 * credential so a user who logs back in on the same device does not have
 * to re-enroll biometrics or re-derive keys for their v2-encrypted data.
 * On the phone it also keeps the cached vault keys, bound to the account
 * signing out, so that account's next sign-in needs no recovery code; any
 * other account signing in gets nothing and the keys are deleted
 * (encryption/vaultKeyCache.ts). The web keeps no vault key.
 */
export interface SessionWipeOptions {
  /**
   * Web only. Leave the 30-day device-trust cookie in place. The idle-lock
   * overlay's "Sign in again" uses this: the lock protects an unattended
   * tab and the password is still required to get back in, so it should not
   * also cost an authenticator user a code after every break. Account →
   * Log out never passes it, which is how a shared computer is forgotten.
   * On the phone the trust token lives in the keychain and is removed with
   * the other tokens either way.
   */
  keepDeviceTrust?: boolean;
  /**
   * The session expired by itself (a 401), nobody chose to sign out: keep
   * unsent letter drafts for the next sign-in instead of removing them.
   */
  sessionExpired?: boolean;
}

export async function wipeSessionOnLogout(options: SessionWipeOptions = {}): Promise<void> {
  logger.info('Wiping session state on logout');
  // First of all: whatever is held above the navigator (Orion's
  // conversation, requests in flight) stops belonging to this session
  // before any of the awaits below gives it time to carry on.
  DeviceEventEmitter.emit(SESSION_ENDED_EVENT);
  // Before the token goes: keep the vault keys for this account only, or
  // delete them (web; or an account that cannot be told). vaultKeyCache.
  await prepareVaultKeysForSignOut();
  // While the token still works: unsent letter drafts go to the account.
  // One that cannot be saved is kept or discarded by the person's choice,
  // never dropped silently (letterDraftMirror).
  await settleLetterDraftsAtSignOut({ sessionExpired: !!options.sessionExpired });
  // While the token is still stored: the server ends this session (and,
  // on the web, clears the session cookie unless the device stays trusted).
  const serverSession = await endServerSession({ keepDeviceTrust: !!options.keepDeviceTrust });
  if (serverSession === 'not-confirmed') {
    // The local wipe still happens; the token simply expires on its own
    // (24 hours at most). Recorded so it is visible in error reports.
    logger.error('Sign-out was not confirmed by the server; the session token was not revoked there');
  }
  await removeCachedProfileImage();
  // "Remember this device for 30 days" survives an ordinary sign-out on the
  // phone (2026-09-23): the next sign-in still needs the password, and the
  // server honours the token only for the account it was issued to. It
  // cannot restore a session by itself: /auth/refresh also needs the
  // session being renewed, which sign-out deletes. The web forgets the
  // computer at sign-out (shared machines); that cookie is server-side.
  const keepPhoneTrust = Platform.OS !== 'web';
  await Promise.all([
    clearSecureStorageTokens(keepPhoneTrust),
    removeAllTokens({ keepDeviceTrust: keepPhoneTrust }), // also scrubs any legacy AsyncStorage token copies
    clearAsyncStorageKeys(),
    errorLoggingService.clearStoredErrors(),
  ]);
  await clearInMemoryCaches();
}

/**
 * Full wipe — used by the account-deletion flow. Destroys everything
 * wipeSessionOnLogout does, PLUS the user's keychain-resident salt
 * (renders all v2 ciphertext on this device unrecoverable) AND the
 * biometric-login credential.
 *
 * After this returns, the device is in a "fresh install" state except
 * for non-user data such as `task_declined_*` preferences.
 */
export async function wipeAllUserData(): Promise<void> {
  logger.info('Wiping all user data (account deletion)');
  await Promise.all([
    clearSecureStorageTokens(),
    removeAllTokens(),
    clearAsyncStorageKeys(ACCOUNT_DELETION_ASYNC_KEYS),
    removeAllCachedProfileImages(),
    errorLoggingService.clearStoredErrors(),
  ]);
  await clearInMemoryCaches();

  // These extra steps are what make account-deletion different from logout.
  await Promise.all([
    deleteUserSalt(),
    biometricService.clearStoredCredentials().catch(() => {
      /* swallow — no credential to clear is fine */
    }),
    SecureStorage.removeItem(SecureStorageKeys.biometricCredentials),
    discardCachedVaultKeys(),
  ]);
}
